import { createI18n } from 'vue-i18n';

/**
 * Load locale messages
 *
 * The loaded `JSON` locale messages is pre-compiled by `@intlify/vue-i18n-loader`, which is integrated into `vue-cli-plugin-i18n`.
 * See: https://github.com/intlify/vue-i18n-loader#rocket-i18n-resource-pre-compilation
 */
function loadLocaleMessages() {
  const locales = require.context('./lang', true, /[A-Za-z0-9-_,\s]+\.json$/i);
  const messages = {};
  locales.keys().forEach((key) => {
    const matched = key.match(/([A-Za-z0-9-_]+)\./i);
    if (matched && matched.length > 1) {
      const locale = matched[1];
      messages[locale] = locales(key);
    }
  });
  return messages;
}

const setDateTimeFormats = {
  short: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
  long: {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
    hour: 'numeric',
    minute: 'numeric',
  },
};

// Number formats for different locales
const setNumberFormats = {
  currency: {
    style: 'currency',
    currency: 'USD',
    notation: 'standard'
  },
  decimal: {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  },
  percent: {
    style: 'percent',
    useGrouping: false
  }
};

const dateTimeFormats = {
  en: setDateTimeFormats,
  sw: setDateTimeFormats, // Swahili
  fr: setDateTimeFormats, // French
  es: setDateTimeFormats, // Spanish
  de: setDateTimeFormats, // German
  ar: setDateTimeFormats, // Arabic
  ch: setDateTimeFormats, // Chinese
  gr: setDateTimeFormats, // Greek
  it: setDateTimeFormats, // Italian
  ru: setDateTimeFormats, // Russian
  sp: setDateTimeFormats, // Spanish (alternative)
  'en-GB': setDateTimeFormats,
};

const numberFormats = {
  en: setNumberFormats,
  sw: {
    ...setNumberFormats,
    currency: { ...setNumberFormats.currency, currency: 'KES' } // Kenyan Shilling for Swahili
  },
  fr: {
    ...setNumberFormats,
    currency: { ...setNumberFormats.currency, currency: 'EUR' } // Euro for French
  },
  es: setNumberFormats,
  de: {
    ...setNumberFormats,
    currency: { ...setNumberFormats.currency, currency: 'EUR' } // Euro for German
  },
  ar: setNumberFormats,
  ch: {
    ...setNumberFormats,
    currency: { ...setNumberFormats.currency, currency: 'CNY' } // Chinese Yuan
  },
  gr: {
    ...setNumberFormats,
    currency: { ...setNumberFormats.currency, currency: 'EUR' } // Euro for Greek
  },
  it: {
    ...setNumberFormats,
    currency: { ...setNumberFormats.currency, currency: 'EUR' } // Euro for Italian
  },
  ru: {
    ...setNumberFormats,
    currency: { ...setNumberFormats.currency, currency: 'RUB' } // Russian Ruble
  },
  sp: setNumberFormats, // Spanish (alternative)
};

// Available languages with metadata
export const SUPPORTED_LOCALES = {
  en: {
    name: 'English',
    flag: '🇺🇸',
    code: 'en',
    rtl: false
  },
  sw: {
    name: 'Kiswahili',
    flag: '🇰🇪',
    code: 'sw',
    rtl: false
  },
  fr: {
    name: 'Français',
    flag: '🇫🇷',
    code: 'fr',
    rtl: false
  },
  ar: {
    name: 'العربية',
    flag: '🇸🇦',
    code: 'ar',
    rtl: true
  },
  ch: {
    name: '中文',
    flag: '🇨🇳',
    code: 'ch',
    rtl: false
  },
  gr: {
    name: 'Ελληνικά',
    flag: '🇬🇷',
    code: 'gr',
    rtl: false
  },
  it: {
    name: 'Italiano',
    flag: '🇮🇹',
    code: 'it',
    rtl: false
  },
  ru: {
    name: 'Русский',
    flag: '🇷🇺',
    code: 'ru',
    rtl: false
  },
  sp: {
    name: 'Español',
    flag: '🇪🇸',
    code: 'sp',
    rtl: false
  }
};

// Get user's preferred locale
function getStartingLocale() {
  const savedLocale = localStorage.getItem('wrti-locale');
  if (savedLocale && SUPPORTED_LOCALES[savedLocale]) {
    return savedLocale;
  }
  
  const browserLocale = navigator.language;
  const browserLang = browserLocale.split('-')[0];
  
  if (SUPPORTED_LOCALES[browserLang]) {
    return browserLang;
  }
  
  return process.env.VUE_APP_I18N_LOCALE || 'en';
}

// Setup document language and direction
function setupI18n(locale) {
  const supportedLocale = SUPPORTED_LOCALES[locale];
  if (supportedLocale) {
    document.documentElement.lang = locale;
    document.documentElement.dir = supportedLocale.rtl ? 'rtl' : 'ltr';
  }
}

const i18n = createI18n({
  legacy: false,
  locale: getStartingLocale(),
  fallbackLocale: process.env.VUE_APP_I18N_FALLBACK_LOCALE || 'en',
  globalInjection: true,
  messages: loadLocaleMessages(),
  dateTimeFormats,
  numberFormats,
});

// Setup initial locale
setupI18n(i18n.global.locale.value);

// Helper functions for locale management
export function setLocale(locale) {
  if (SUPPORTED_LOCALES[locale]) {
    i18n.global.locale.value = locale;
    localStorage.setItem('wrti-locale', locale);
    setupI18n(locale);
  }
}

export function getCurrentLocale() {
  return i18n.global.locale.value;
}

export function getAvailableLocales() {
  return Object.keys(SUPPORTED_LOCALES);
}

export function getLocaleInfo(locale) {
  return SUPPORTED_LOCALES[locale];
}

export default i18n;